import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { PIPELINE } from "@/data/site";
import { MaskedLines, SectionTag } from "@/components/Kinetic";
import { EASE, viewport } from "@/lib/motion";

const Step = ({ step, i, last, progress, accent, stagger }) => {
  const range = [i * stagger, i * stagger + 0.12];
  const opacity = useTransform(progress, range, [0.18, 1]);
  const x = useTransform(progress, range, [16, 0]);

  return (
    <div className="flex items-center">
      <motion.span
        style={{ opacity, x, borderColor: accent ? "#d4ff00" : "#1f1f1f" }}
        className="border px-3 py-2 label md:px-4 md:py-2.5"
        data-testid={`${accent ? "ai" : "trad"}-step-${step.toLowerCase()}`}
      >
        <span style={{ color: accent ? "#d4ff00" : "#b3b3b3" }}>{step}</span>
      </motion.span>
      {!last && (
        <motion.span
          style={{ opacity, background: accent ? "#d4ff00" : "#1f1f1f" }}
          className="mx-1 h-[1px] w-4 md:w-8"
        />
      )}
    </div>
  );
};

const Lane = ({ label, note, progress, accent, stagger }) => (
  <div className="border-t border-[#1f1f1f] py-8 md:py-10">
    <div className="mb-6 flex flex-col gap-2 md:flex-row md:items-baseline md:justify-between">
      <span className="label" style={{ color: accent ? "#d4ff00" : "#a3a3a3" }}>
        {label}
      </span>
      <span className="label text-[#7a7a7a]">{note}</span>
    </div>
    <div className="flex flex-wrap items-center gap-y-4">
      {PIPELINE.map((step, i) => (
        <Step
          key={step}
          step={step}
          i={i}
          last={i === PIPELINE.length - 1}
          progress={progress}
          accent={accent}
          stagger={stagger}
        />
      ))}
    </div>
  </div>
);

export const SpeedPipeline = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.85", "end 0.35"],
  });
  const smooth = useSpring(scrollYProgress, { stiffness: 90, damping: 26, mass: 0.4 });
  const barWidth = useTransform(smooth, [0, 1], ["0%", "100%"]);

  return (
    <section
      ref={ref}
      data-testid="speed-pipeline"
      className="relative border-t border-[#1f1f1f] py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1700px] px-5 md:px-10">
        <SectionTag num="05 / VELOCITY">TURNAROUND</SectionTag>

        <div className="grid gap-10 lg:grid-cols-12">
          <MaskedLines
            lines={["SPEED ISN'T ABOUT", "RUSHING THE EDIT.", "IT'S ABOUT REMOVING", "THE WAITING."]}
            className="font-display text-3xl font-black uppercase leading-[0.9] tracking-[-0.04em] text-[#e5e5e5] lg:col-span-8 md:text-[3.6vw]"
            testId="speed-headline"
          />
          <p className="max-w-sm self-end text-sm leading-relaxed text-[#a3a3a3] lg:col-span-4">
            Turnaround depends on the project. The workflow doesn't have to be
            slow. Same seven stages — the difference is how long each one sits
            in a queue.
          </p>
        </div>

        <div className="relative mt-16 md:mt-24">
          <div className="absolute -top-8 left-0 h-[2px] w-full bg-[#1f1f1f]">
            <motion.div style={{ width: barWidth }} className="h-full bg-[#d4ff00]" />
          </div>

          <Lane
            label="TRADITIONAL WORKFLOW"
            note="EVERY STAGE WAITS FOR THE LAST ONE"
            progress={smooth}
            stagger={0.13}
            accent={false}
          />
          <Lane
            label="AI-ASSISTED WORKFLOW"
            note="STAGES OVERLAP — EXPLORATION HAPPENS IN PARALLEL"
            progress={smooth}
            stagger={0.045}
            accent
          />
          <div className="border-t border-[#1f1f1f]" />
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewport}
          transition={{ duration: 0.9, ease: EASE }}
          className="mt-14 max-w-3xl font-display text-lg font-bold leading-snug tracking-tight text-[#e5e5e5] md:text-2xl"
        >
          Your content has a shelf life. The edit shouldn't be what slows it
          down.
        </motion.p>
      </div>
    </section>
  );
};
